import { React, ReactRouter, useAPI } from 'mcelroy-lib'
import { FormPage, useUserContext, useToast } from 'mcelroy-components'

/*
    User profile page
*/
export function UserProfilePage() {
    const toast = useToast()
    const routeParams = ReactRouter.useParams()
    const navigate = ReactRouter.useNavigate()
    const { user: currentUser } = useUserContext()
    const [isEditMode, setIsEditMode] = React.useState(false)

    // API user info query
    const { loading, data, error, rerun: rerunUserLoad } = useAPI(`
        query($user_id: Int){
            user(id: $user_id){
                id firstName lastName email customerNbr
                permissions{
                    employee vendor customer consumer
                    fulladmin useradmin custadmin
                    permOrderEntry permOrderInquiry permAccountStatus permInvoiceInquiry
                    customerOverride
                }
                roles
                vendorNumber
            }
        }
    `, {user_id: routeParams.user_id})

    // API logout mutation
    const { run: runLogout } = useAPI(`
        mutation{ logout{id} }
    `)

    // API update user mutation
    const { run: updateUser } = useAPI(`
        mutation($user: UserInput!){
            updateUser( user:$user ){id}
        }
    `)

    // API delete user mutation
    const { run: deleteUser } = useAPI(`
        mutation($id: Int!){
            deleteUser( id:$id)
        }
    `)

    const user = data?.user
    const permissions = user?.permissions
    const userType = getUserType(permissions)

    // Form fields
    const fields = [
        {
            label: '', // user section without label
            fields: [
                {
                    label: 'User Type', disabled: !isEditMode || permissions.employee, name: 'userType', value: userType,
                    show: currentUser.isUserAdmin,
                    options: [
                        { label: 'Employee', value: 'Employee', disabled: true },
                        { label: 'Distributor', value: 'Distributor' },
                        { label: 'Vendor', value: 'Vendor', disabled: !currentUser.isEmployee },
                        { label: 'Consumer', value: 'Consumer' },
                    ]
                },

                { label: 'First Name', name: 'firstName', value: user?.firstName, disabled: !isEditMode || permissions?.employee },
                { label: 'Last Name', name: 'lastName', value: user?.lastName, disabled: !isEditMode || permissions?.employee },
                { label: 'Email', name: 'email', value: user?.email, disabled: true },

                {
                    label: 'Distributor Number', name: 'customerOverride', value: permissions?.customerOverride, disabled: !isEditMode,
                    validator: (v) => { return Number.isInteger(+v) ? null : 'Must be integer value' },
                    helperText: 'Customer Override',
                    show: currentUser.isEmployee && userType === 'Employee'
                },
                {
                    label: 'Distributor Number', name: 'customerNbr', value: user?.customerNbr, disabled: !isEditMode,
                    validator: (v) => { return Number.isInteger(+v) ? null : 'Must be integer value' },
                    show: currentUser.isEmployee && userType === 'Distributor'
                },
                {
                    label: 'Distributor Number', name: 'customerNbr', value: user?.customerNbr, disabled: !isEditMode,
                    options: currentUser.accounts.map((aId) => {return {label: aId, value: aId}}),
                    show: currentUser.isDistributor && userType === 'Distributor'
                },

                {
                    label: 'Vendor Number', name: 'vendorNumber', disabled: !isEditMode,
                    value: user?.vendorNumber,
                    show: currentUser.isUserAdmin && userType === 'Vendor'
                }
            ]
        },
        {
            label: 'Permissions', // permissions section,
            show: currentUser.isUserAdmin && (userType === 'Employee' || userType === 'Distributor'),
            fields: [
                {
                    label: 'User Admin', name: 'useradmin', type: 'checkbox', disabled: !isEditMode,
                    value: isUserAdmin(permissions),
                    show: userType === 'Employee' || userType === 'Distributor'
                },
                {
                    label: 'Patent Admin', name: 'PatentAdmin', type: 'checkbox', disabled: !isEditMode,
                    value: user?.roles?.includes('PatentAdmin'),
                    show: currentUser.hasRoles(['PatentAdmin']) && userType === 'Employee'
                },
                {
                    label: 'Distributor Portal',
                    indent: true,
                    show: userType === 'Employee' || userType === 'Distributor',
                    fields: [
                        {
                            label: 'Order Entry', name: 'permOrderEntry', type: 'checkbox', value: permissions?.permOrderEntry, disabled: !isEditMode,
                            show: userType === 'Employee' || userType === 'Distributor'
                        },
                        {
                            label: 'Order Inquiry', name: 'permOrderInquiry', type: 'checkbox', value: permissions?.permOrderInquiry, disabled: !isEditMode,
                            show: userType === 'Employee' || userType === 'Distributor'
                        },
                        {
                            label: 'Invoice Inquiry', name: 'permInvoiceInquiry', type: 'checkbox', value: permissions?.permInvoiceInquiry, disabled: !isEditMode,
                            show: userType === 'Employee' || userType === 'Distributor'
                        },
                        {
                            label: 'Account Status', name: 'permAccountStatus', type: 'checkbox', value: permissions?.permAccountStatus, disabled: !isEditMode,
                            show: userType === 'Employee' || userType === 'Distributor'
                        }
                    ]
                },
                {
                    label: 'Vendor Portal',
                    indent: true,
                    show: currentUser.hasRoles(['VPAdmin']) && userType === 'Employee',
                    fields: [
                        {
                            label: 'Admin', name: 'VPAdmin', type: 'checkbox', disabled: !isEditMode,
                            value: user?.roles?.includes('VPAdmin')
                        },
                        {
                            label: 'Buyer', name: 'VPBuyer', type: 'checkbox', disabled: !isEditMode,
                            value: user?.roles?.includes('VPBuyer')
                        },
                        {
                            label: 'User', name: 'VPRegUser', type: 'checkbox', disabled: !isEditMode,
                            value: user?.roles?.includes('VPRegUser')
                        }
                    ]
                }
            ]
        }
    ]

    // Form buttons
    const buttons = []
    createButton(buttons, data?.user && isEditMode, 'Save', PRIMARY, async (formData) => {
        try {
            const profile = { ...data?.user, ...formData }
            profile.permissions = { ...data.user.permissions, ...formData }
            setUserType(profile.permissions, formData.userType)
            setRoles(profile, formData, ['VPAdmin', 'VPBuyer', 'VPRegUser', 'PatentAdmin'])
            await updateUser({ user: profile })
            setIsEditMode(false)
            await rerunUserLoad()
            toast.success('Account saved')
        } catch (err) {
            if(err)
                toast.error(err[0]?.message ?? 'Error updating user.')
            else
                toast.error('Error saving user account')
            console.log(err)
        }
    })
    createButton(buttons, data?.user && isEditMode, 'Cancel', PLAIN, async () => {
        setIsEditMode(false)
        await rerunUserLoad()
    })
    createButton(buttons, data?.user && !isEditMode, 'Edit', PLAIN, () => { setIsEditMode(true) })
    createButton(buttons, !isEditMode && userType != 'Employee', 'Change Password', PLAIN, () => {
        navigate('/changePassword/' + data?.user?.id)
    })
    createButton(buttons, currentUser.id == data?.user?.id && !isEditMode, 'Log Out', PLAIN, async () => {
        await runLogout()
        navigate('/')
        window.location.reload()
    })
    createButton(buttons, isEditMode && currentUser.isUserAdmin && currentUser.id != data?.user?.id, 'Delete', CAUTION, async () => {
        if (confirm('This can not be undone.  Delete user?')) {
            await deleteUser({ id: data?.user?.id })
            toast.success('User deleted.')
            navigate('/users')
        }
    })

    const formSize = userType === 'Consumer' || userType === 'Vendor' || !currentUser.isUserAdmin ? 'sm' : 'md'

    // Page display
    if (loading)
        return null
    else if (error)
        return (<div>Not authorized</div>)
    else return (
        <FormPage size={formSize} title="Profile" fields={fields} buttons={buttons} />
    )
}

/*
    Helper function to create form buttons
*/
const PLAIN = 0
const PRIMARY = 1
const CAUTION = 2

function createButton(buttons, add, label, level, fun) {
    if (add) {
        buttons.push({
            label: label,
            primary: level === PRIMARY ? true : false,
            caution: level === CAUTION ? true : false,
            onClick: fun
        })
    }
}

/*
    In database the type is in multiple columns which means an account can be.
    Changing that in the UI so that you can only select one to keep from having
    mistakes where a distributor was also marked as an employee
*/
function getUserType(permissions) {
    return permissions?.employee ? 'Employee' :
        permissions?.customer ? 'Distributor' :
            permissions?.vendor ? 'Vendor' : 'Consumer'
}

/*
    Change user type back into multiple column permissions in database
    if employeeType is passed otherwise just leave it alone.
    Non admins don't have the user type field so it will be null for them.
*/
function setUserType(permissions, employeeType) {
    if (employeeType != null) {
        permissions.employee = employeeType === 'Employee' ? true : false
        permissions.customer = employeeType === 'Distributor' ? true : false
        permissions.vendor = employeeType === 'Vendor' ? true : false
        permissions.consumer = employeeType === 'Consumer' ? true : false
    }
}

/*
    Set roles based on check boxes
*/
function setRoles(profile, formData, formRoles) {
    // clear out roles
    const roles = profile.roles.split(',').filter((v) => { return !formRoles.includes(v) })

    // add roles back in if checked in formData
    for (const r of formRoles) {
        if(formData[r])
            roles.push(r)
    }
    
    // update roles to new set
    profile.roles = roles
}
   

/*
    Database has different user admin options depending on if you are an
    employee or distributor - but this allows for a distributor to be
    marked with employee permissions so just keep everything as useradmin
    and the backend API will mark the correct permission based on user type.
*/
function isUserAdmin(permissions) {
    return permissions?.fulladmin || permissions?.useradmin || permissions?.custadmin
}
